import type { LevelAssessmentModel } from "@shared/labs/level-assessment-model";
import { Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { rpcClient } from "@/lib/rpc";

/** The optional Level-Assessment scope for a search. All null → generic
 *  sourcing (identical to today). `level` is the recruiter's target level from
 *  the model's matrix (only meaningful once a model is chosen). */
export interface LaScopeSelection {
  modelId: string | null;
  disciplineId: string | null;
  level: number | null;
}

export const EMPTY_LA_SCOPE: LaScopeSelection = {
  modelId: null,
  disciplineId: null,
  level: null,
};

// Radix Select forbids an empty-string item value, so a sentinel stands in for
// the "none" option and is mapped back to null at the boundary.
const NONE = "__none__";

/**
 * Pre-search qualifier (Step 01): optionally scope the search to a Level-
 * Assessment model + discipline. This is purely additive — leaving it on
 * "Nenhum" makes the search byte-identical to a non-enriched one. When a model
 * is picked, the backend harvests LA's read-only artifacts and the requirements
 * + ranking are aligned to the in-scope competencies (shown in the hint).
 *
 * The control only READS LA's model list; the selection is written to sourcing's
 * own search row when the search starts.
 */
export function SourcingModelPicker({
  value,
  onChange,
  enableLevel = false,
}: {
  value: LaScopeSelection;
  onChange: (next: LaScopeSelection) => void;
  /** Show the target-level selector (Explore flow). Off → model+discipline only
   *  (classic search, unchanged). */
  enableLevel?: boolean;
}) {
  const [models, setModels] = useState<LevelAssessmentModel[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = (await rpcClient.labs.levelAssessmentModelsList({})) as {
          models: LevelAssessmentModel[];
        };
        if (!cancelled) setModels(res.models);
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedModel = value.modelId
    ? (models?.find((m) => m.id === value.modelId) ?? null)
    : null;
  const disciplines = selectedModel?.disciplines ?? [];
  const hasDisciplines = disciplines.length > 0;

  // In-scope competency labels for the transparency hint: cross axes always,
  // plus the chosen discipline's vertical axes.
  const crossLabels = selectedModel?.crossAxes.map((a) => a.label) ?? [];
  const disciplineLabels = value.disciplineId
    ? (disciplines.find((d) => d.id === value.disciplineId)?.axes.map((a) => a.label) ?? [])
    : [];
  const inScope = [...crossLabels, ...disciplineLabels];

  // Selectable levels: the discipline's sub-band when one is chosen and defined,
  // else the model's full level range. Labels use the model's custom level names.
  const selectedDiscipline = value.disciplineId
    ? (disciplines.find((d) => d.id === value.disciplineId) ?? null)
    : null;
  const levelRange = selectedDiscipline?.levels ?? selectedModel?.levels ?? null;
  const levelNames = selectedModel?.levels.names ?? {};
  const levelOptions = levelRange
    ? Array.from({ length: levelRange.max - levelRange.min + 1 }, (_, i) => {
        const n = levelRange.min + i;
        return { value: n, label: levelNames[String(n)] ?? `L${n}` };
      })
    : [];

  return (
    <div
      data-testid="sourcing-model-picker"
      className="rounded-xl border border-border bg-card px-4 py-3.5"
    >
      <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
        Modelo de nível <span className="opacity-60">(opcional)</span>
      </span>
      <p className="mt-1.5 max-w-xl text-[13px] leading-relaxed text-muted-foreground">
        Ancore os requisitos e o ranqueamento nas competências de um modelo de Level Assessment. Em
        branco, a busca é genérica — idêntica a hoje.
      </p>

      <div className="mt-3 flex flex-wrap items-center gap-2.5">
        <Select
          value={value.modelId ?? NONE}
          onValueChange={(v) =>
            // Switching the model invalidates any discipline + level → reset them.
            onChange({ modelId: v === NONE ? null : v, disciplineId: null, level: null })
          }
          disabled={models === null}
        >
          <SelectTrigger
            data-testid="sourcing-model-select"
            className="h-9 min-w-[260px] text-[13px]"
          >
            <SelectValue placeholder="Carregando modelos…" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>Nenhum (sourcing genérico)</SelectItem>
            {(models ?? []).map((m) => (
              <SelectItem key={m.id} value={m.id} data-testid={`sourcing-model-option-${m.id}`}>
                <span className="inline-flex items-center gap-2">
                  <span className="font-medium">{m.name}</span>
                  <span className="text-[11px] tabular-nums text-muted-foreground">
                    {m.crossAxes.length} competências
                    {m.disciplines.length > 0 ? ` · ${m.disciplines.length} disciplinas` : ""}
                  </span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {selectedModel && hasDisciplines && (
          <Select
            value={value.disciplineId ?? NONE}
            onValueChange={(v) =>
              // A discipline can carry its own level sub-band → reset the level.
              onChange({ modelId: value.modelId, disciplineId: v === NONE ? null : v, level: null })
            }
          >
            <SelectTrigger
              data-testid="sourcing-discipline-select"
              className="h-9 min-w-[240px] text-[13px]"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>Nenhuma (só transversais)</SelectItem>
              {disciplines.map((d) => (
                <SelectItem
                  key={d.id}
                  value={d.id}
                  data-testid={`sourcing-discipline-option-${d.id}`}
                >
                  {d.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {enableLevel && selectedModel && levelOptions.length > 0 && (
          <Select
            value={value.level != null ? String(value.level) : NONE}
            onValueChange={(v) =>
              onChange({
                modelId: value.modelId,
                disciplineId: value.disciplineId,
                level: v === NONE ? null : Number(v),
              })
            }
          >
            <SelectTrigger
              data-testid="sourcing-level-select"
              className="h-9 min-w-[180px] text-[13px]"
            >
              <SelectValue placeholder="Nível-alvo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>Qualquer nível</SelectItem>
              {levelOptions.map((l) => (
                <SelectItem
                  key={l.value}
                  value={String(l.value)}
                  data-testid={`sourcing-level-option-${l.value}`}
                >
                  {l.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {error && (
        <p className="mt-2 text-[12px] text-destructive">
          Não foi possível carregar os modelos de nível.
        </p>
      )}

      {selectedModel && inScope.length > 0 && (
        <p
          data-testid="sourcing-model-hint"
          className="mt-3 flex items-start gap-2 text-[12.5px] leading-relaxed text-muted-foreground"
        >
          <Sparkles size={14} className="mt-0.5 shrink-0 opacity-70" />
          <span>
            Requisitos e ranqueamento também considerarão:{" "}
            <span className="text-foreground">{inScope.join(", ")}</span>.
          </span>
        </p>
      )}
    </div>
  );
}
